<script setup lang="ts">
// Preload da fonte-base. Exo-2 é aplicada por `* { font-family }` abaixo, então
// é a primeira fonte que qualquer texto da página precisa — sem preload ela só
// começa a baixar depois que o CSS é parseado, e o texto pinta com a fallback.
// O import passa pelo Vite, então a URL aqui é exatamente a mesma (com hash) que
// o `url()` do @font-face gera — se fossem strings diferentes, o browser baixaria
// a fonte duas vezes.
import exo2Url from "~/assets/fonts/Exo-2-Regular.woff2";

// Token do origin trial do WebMCP (Chrome 149+). Vem do runtimeConfig, e não
// fixo no código, para poder ser rotacionado/removido sem rebuild — o token
// expira e é atrelado à origem. Sem ele a API só existe para quem ligou a flag
// chrome://flags/#enable-webmcp-testing.
const webmcpOriginTrial = useRuntimeConfig().public.webmcpOriginTrialToken;

// De onde a visita veio (UTMs, página de entrada, acionador): vai no checkout para o servidor
// gravar a origem da venda sem depender do GA4.
useOrigemDaVisita();

useHead({
  link: [
    {
      rel: "preload",
      as: "font",
      type: "font/woff2",
      href: exo2Url,
      // Obrigatório: fonte é sempre buscada em modo CORS, e sem isto o preload
      // não casa com a requisição do @font-face (baixaria duas vezes).
      crossorigin: "anonymous",
    },
  ],
  meta: webmcpOriginTrial
    ? [{ "http-equiv": "origin-trial", content: String(webmcpOriginTrial) }]
    : [],
});
</script>

<template>
  <NuxtLoadingIndicator color='#b20000' />
  <NuxtPage />
</template>
<style>
/*
 * Fontes em .woff2 (eram .ttf: 1,21 MB em disco -> 246 KB, mesmos glifos).
 * `font-display: swap` em todas: .ttf sem swap bloqueia a pintura do texto
 * durante o download e entra direto no LCP.
 *
 * Removidas por não terem nenhum `font-family` apontando para elas:
 * American-Typewriter, Bebas-Neue-Thin, Araboto-Medium (esta última já estava
 * quebrada em produção — o arquivo .ttf não existe no repositório e a URL
 * respondia 404), além dos arquivos órfãos Roboto-Light.ttf e Roboto-Thin.ttf,
 * que nem @font-face tinham.
 */
@font-face {
  font-family: "Exo-2";
  src: local("Exo-2"), url(./assets/fonts/Exo-2-Regular.woff2) format("woff2");
  font-display: swap;
}

@font-face {
  font-family: "Bebas-Neue";
  src: local("Bebas-Neue"),
    url(./assets/fonts/BebasNeueRegular.woff2) format("woff2");
  font-display: swap;
}

@font-face {
  font-family: "Bebas-Neue-Light";
  src: local("Bebas-Neue-Light"),
    url(./assets/fonts/BebasNeueLight.woff2) format("woff2");
  font-display: swap;
}

@font-face {
  font-family: "Roboto-Medium";
  src: local("Roboto-Medium"),
    url(./assets/fonts/Roboto-Medium.woff2) format("woff2");
  font-display: swap;
}

@font-face {
  font-family: "Roboto-Regular";
  src: local("Roboto-Regular"),
    url(./assets/fonts/Roboto-Regular.woff2) format("woff2");
  font-display: swap;
}

@font-face {
  font-family: "Roboto-Bold";
  src: local("Roboto-Bold"),
    url(./assets/fonts/Roboto-Bold.woff2) format("woff2");
  font-display: swap;
}

/* Fallback explícita: com `swap` o texto pinta na fallback antes da Exo-2 chegar,
   e sem `sans-serif` aqui essa primeira pintura usava a serifada padrão do browser. */
* {
  font-family: "Exo-2", sans-serif;
}

body{
  background-color: #000;
}
</style>
